import type {
  Configuration,
  AccountInfo,
  SilentRequest,
  RedirectRequest,
} from "@azure/msal-browser";
import {
  PublicClientApplication,
  BrowserCacheLocation,
  EventType,
  InteractionRequiredAuthError,
  type AuthenticationResult,
} from "@azure/msal-browser";
import { appConfigService } from "@/services/appConfigService";

/**
 * Authentication Service using Microsoft Authentication Library (MSAL)
 * Handles user sign-in, token acquisition, and logout
 */
let msalInstance: PublicClientApplication | null = null;
let initializationPromise: Promise<PublicClientApplication> | null = null;
let redirectInProgress = false;

const ACTIVE_TENANT_KEY = "mci-wiki-active-tenant-id";
const POST_LOGIN_REDIRECT_KEY = "mci-wiki-post-login-redirect";
const AUTH_REDIRECT_PATH = "/redirect";
const DEFAULT_POST_LOGIN_REDIRECT_PATH = "/en-US";

function getMsalRedirectUri(): string {
  return new URL(AUTH_REDIRECT_PATH, window.location.origin).toString();
}

function sanitizePostLoginRedirectPath(candidate?: string | null): string {
  if (!candidate) {
    return DEFAULT_POST_LOGIN_REDIRECT_PATH;
  }

  try {
    const url = new URL(candidate, window.location.origin);

    if (
      url.origin !== window.location.origin ||
      url.pathname === AUTH_REDIRECT_PATH ||
      url.pathname.startsWith("//")
    ) {
      return DEFAULT_POST_LOGIN_REDIRECT_PATH;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return DEFAULT_POST_LOGIN_REDIRECT_PATH;
  }
}

function getCurrentPostLoginRedirectPath(): string {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return sanitizePostLoginRedirectPath(currentPath);
}

function storePostLoginRedirectPath(path: string = getCurrentPostLoginRedirectPath()): string {
  const safePath = sanitizePostLoginRedirectPath(path);
  sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, safePath);
  return new URL(safePath, window.location.origin).toString();
}

function consumePostLoginRedirectPath(): string {
  const safePath = sanitizePostLoginRedirectPath(sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY));
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  return safePath;
}

function ensureActiveAccount(instance: PublicClientApplication): void {
  const active = instance.getActiveAccount();
  if (active) {
    return;
  }

  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) {
    return;
  }

  // Restore the previously selected tenant account (e.g., after tenant switch + page refresh)
  const storedTenantId = sessionStorage.getItem(ACTIVE_TENANT_KEY);
  if (storedTenantId) {
    const match = accounts.find((a) => a.tenantId === storedTenantId);
    if (match) {
      instance.setActiveAccount(match);
      return;
    }
  }

  instance.setActiveAccount(accounts[0]);
}

async function triggerInteractiveReauth(account?: AccountInfo | null): Promise<void> {
  if (!msalInstance || redirectInProgress) {
    return;
  }

  redirectInProgress = true;

  const config = await appConfigService.getAppEnvironmentData();

  try {
    // Prefer acquireTokenRedirect to re-establish a valid token for an existing account.
    const fallbackAccount =
      account ?? msalInstance.getActiveAccount() ?? (msalInstance.getAllAccounts()[0] || null);
    if (!fallbackAccount) {
      console.error("No account available for token acquisition.");
      return;
    }
    await msalInstance.acquireTokenRedirect({
      scopes: config.scopes,
      account: fallbackAccount,
      redirectStartPage: storePostLoginRedirectPath(),
    });
  } catch {
    // Fallback to a full login if token redirect fails.
    await msalInstance.loginRedirect({
      scopes: config.scopes,
      prompt: "select_account",
      redirectStartPage: storePostLoginRedirectPath(),
      extraQueryParameters: {
        max_age: "3600",
      },
    });
  } finally {
    // Reset the redirectInProgress flag after the redirect attempt
    redirectInProgress = false;
  }
}

/**
 * Gets the current access token silently
 */
async function getAccessToken(forceRefresh: boolean = false): Promise<string | null> {
  if (!msalInstance) {
    console.error("getAccessToken: MSAL instance not initialized - call createAuthProvider first");
    return null;
  }

  try {
    ensureActiveAccount(msalInstance);
    const accounts = msalInstance.getAllAccounts();
    const config = await appConfigService.getAppEnvironmentData();

    if (accounts.length === 0) {
      console.error("getAccessToken: No accounts found, user needs to login");

      await msalInstance.loginRedirect({
        scopes: config.scopes,
        prompt: "select_account",
        redirectStartPage: storePostLoginRedirectPath(),
      });
      return null;
    }

    const account = msalInstance.getActiveAccount() ?? accounts[0];

    const tokenRequest: SilentRequest = {
      scopes: config.scopes,
      account,
      authority: account.tenantId
        ? `https://login.microsoftonline.com/${account.tenantId}/`
        : undefined,
      forceRefresh,
    };

    const response = await msalInstance.acquireTokenSilent(tokenRequest);

    const token = response.accessToken;

    if (!token) {
      console.error("getAccessToken: No access token or ID token available in response");
      return null;
    }

    return token;
  } catch (error) {
    // Silent token acquisition can fail when the browser blocks silent auth
    // (e.g., third-party cookie restrictions) or when the refresh context is expired.
    if (error instanceof InteractionRequiredAuthError) {
      await triggerInteractiveReauth(
        msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
      );
      return null;
    }

    console.error("getAccessToken: Token acquisition failed:", error);
    return null;
  }
}

/**
 * Clears cache and logs out the user
 */
async function clearCacheAndLogout(): Promise<void> {
  if (!msalInstance) {
    return;
  }

  try {
    sessionStorage.removeItem(ACTIVE_TENANT_KEY);
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  } catch (error) {
    console.error("Error during logout:", error);
    window.location.reload();
  }
}

/**
 * Switches to a different account by clearing cached accounts and showing account picker.
 * Clears MSAL's cached accounts first to ensure fresh state, then uses prompt: "select_account"
 * to show the account picker, allowing the user to choose a different account.
 */
async function switchAccount(): Promise<void> {
  if (!msalInstance) {
    msalInstance = await createAuthProvider();
  }

  try {
    const config = await appConfigService.getAppEnvironmentData();

    // Clear active account to ensure fresh state
    msalInstance.setActiveAccount(null);

    // Clear MSAL cache entries from sessionStorage
    const clientId = config.aadClientId;
    const sessionKeysToRemove: string[] = [];

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.includes(clientId) || key.startsWith("msal."))) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key));

    // Also clear localStorage in case of any stale entries from config changes
    const localKeysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes(clientId) || key.startsWith("msal."))) {
        localKeysToRemove.push(key);
      }
    }
    localKeysToRemove.forEach((key) => localStorage.removeItem(key));

    // Show account picker with clean state
    await msalInstance.loginRedirect({
      scopes: config.scopes,
      redirectStartPage: storePostLoginRedirectPath(),
      prompt: "select_account", // Shows "Pick an account" prompt
    });
  } catch (error) {
    console.error("Error during account switch:", error);
    throw error;
  }
}

/**
 * Creates and initializes the MSAL authentication provider
 */
async function createAuthProvider(tenantId?: string): Promise<PublicClientApplication> {
  if (msalInstance) {
    return msalInstance;
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async (): Promise<PublicClientApplication> => {
    const envData = await appConfigService.getAppEnvironmentData();

    // Only allow tenant override in non-production environments
    // and only if the tenant ID is in the allowed list.
    let authorityTenantId = envData.aadTenantId;
    if (tenantId && envData.env !== "prod") {
      const allowedTenantIds = envData.allowedTenantIds ?? [];
      if (allowedTenantIds.some((id) => id.toLowerCase() === tenantId.toLowerCase())) {
        authorityTenantId = tenantId;
      } else {
        console.warn(
          `Tenant ID "${tenantId}" is not in the allowed list. Using default tenant.`
        );
      }
    }

    const config: Configuration = {
      auth: {
        clientId: envData.aadClientId,
        authority: `https://login.microsoftonline.com/${authorityTenantId}/`,
        redirectUri: getMsalRedirectUri(),
        postLogoutRedirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: BrowserCacheLocation.SessionStorage,
      },
      system: {
        loggerOptions: {
          loggerCallback: (level: number, message: string, containsPii: boolean): void => {
            if (containsPii) {
              return;
            }
            switch (level) {
              case 0:
                console.error("[MSAL Error]", message);
                break;
              case 1:
                console.warn("[MSAL Warning]", message);
                break;
              default:
                break;
            }
          },
          piiLoggingEnabled: false,
        },
        redirectNavigationTimeout: 60000,
      },
    };

    const instance = new PublicClientApplication(config);
    await instance.initialize();

    instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        const payload = event.payload as AuthenticationResult | null;
        if (payload?.account) {
          instance.setActiveAccount(payload.account);
          sessionStorage.setItem(ACTIVE_TENANT_KEY, payload.account.tenantId);
        }
      }
    });

    try {
      const response = await instance.handleRedirectPromise();
      if (response?.account) {
        instance.setActiveAccount(response.account);
        sessionStorage.setItem(ACTIVE_TENANT_KEY, response.account.tenantId);
      }
    } catch (error) {
      console.error("Error handling redirect:", error);
    }

    ensureActiveAccount(instance);

    msalInstance = instance;
    initializationPromise = null;
    return instance;
  })();

  return initializationPromise;
}

/**
 * Gets the current logged-in account.
 * Prefers the active account (set after redirect) over the first cached account,
 * which is important after tenant switching where multiple accounts may be cached.
 */
function getCurrentAccount(): AccountInfo | null {
  if (!msalInstance) {
    return null;
  }

  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

/**
 * Initiates the login flow
 */
async function login(): Promise<void> {
  if (!msalInstance) {
    msalInstance = await createAuthProvider();
  }

  const config = await appConfigService.getAppEnvironmentData();

  const loginRequest: RedirectRequest = {
    scopes: config.scopes,
    prompt: "select_account",
    redirectStartPage: storePostLoginRedirectPath(),
    extraQueryParameters: {
      max_age: "3600",
    },
  };

  try {
    await msalInstance.loginRedirect(loginRequest);
  } catch (error) {
    console.error("Login failed:", error);
    throw error;
  }
}

/**
 * Switches the user to a different Azure AD tenant/directory.
 * Uses login_hint to skip the account picker for a seamless switch (like Partner Center).
 */
async function switchTenant(tenantId: string): Promise<void> {
  if (!msalInstance) {
    msalInstance = await createAuthProvider();
  }

  const currentAccount = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

  await msalInstance.loginRedirect({
    authority: `https://login.microsoftonline.com/${tenantId}`,
    scopes: ["openid", "profile", "offline_access"],
    loginHint: currentAccount?.username,
    redirectStartPage: storePostLoginRedirectPath(),
  });
}

/**
 * Checks if user is authenticated
 */
function isAuthenticated(): boolean {
  return getCurrentAccount() !== null;
}

/**
 * Gets an access token for Microsoft Graph API
 * Falls back to popup consent if user hasn't consented to User.Read scope
 */
async function getGraphAccessToken(): Promise<string | null> {
  if (!msalInstance) {
    console.error("getGraphAccessToken: MSAL instance not initialized");
    return null;
  }

  try {
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
      console.error("getGraphAccessToken: No accounts found");
      return null;
    }

    const account = msalInstance.getActiveAccount() ?? accounts[0];

    const tokenRequest: SilentRequest = {
      scopes: ["User.Read"],
      account,
      authority: account.tenantId
        ? `https://login.microsoftonline.com/${account.tenantId}/`
        : undefined,
      forceRefresh: false,
    };

    try {
      const response = await msalInstance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (silentError) {
      // If silent acquisition fails due to consent required, try popup
      if (silentError instanceof InteractionRequiredAuthError) {
        try {
          const response = await msalInstance.acquireTokenPopup({
            scopes: ["User.Read"],
            account,
          });
          return response.accessToken;
        } catch (popupError) {
          console.error("getGraphAccessToken: Popup consent failed:", popupError);
          return null;
        }
      }
      throw silentError;
    }
  } catch (error) {
    console.error("getGraphAccessToken: Token acquisition failed:", error);
    return null;
  }
}

/**
 * Fetches the user's profile photo from Microsoft Graph
 * Returns a blob URL or null if not available
 */
async function getProfilePhoto(): Promise<string | null> {
  try {
    const token = await getGraphAccessToken();
    if (!token) {
      return null;
    }

    const response = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      // 404 means user has no profile photo
      if (response.status === 404) {
        return null;
      }
      console.error("Failed to fetch profile photo:", response.status);
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error fetching profile photo:", error);
    return null;
  }
}

export const authService = {
  createAuthProvider,
  login,
  consumePostLoginRedirectPath,
  getAccessToken,
  getCurrentAccount,
  clearCacheAndLogout,
  switchAccount,
  switchTenant,
  isAuthenticated,
  getGraphAccessToken,
  getProfilePhoto,
};

// Export msalInstance for use in MsalProvider (will be initialized before use)
export { msalInstance };
